import { useState, useEffect, useCallback } from 'react';
import { usCustomsRateAPI, customsBrokerFeeAPI } from '../../services/api';

/**
 * UsCustomsConfig — Admin page for managing US Customs Rates and Customs Broker Fees.
 * Two independent CRUD tables following the same inline-edit pattern as TruckingParameters.
 */
const UsCustomsConfig = () => {
  // --- US Customs Rates state ---
  const [customsRates, setCustomsRates] = useState([]);
  const [customsLoading, setCustomsLoading] = useState(true);
  const [customsError, setCustomsError] = useState(null);
  const [customsEditingId, setCustomsEditingId] = useState(null);
  const [customsEditForm, setCustomsEditForm] = useState({});
  const [customsSaving, setCustomsSaving] = useState(false);
  const [customsFeedback, setCustomsFeedback] = useState(null);
  const [showCustomsAdd, setShowCustomsAdd] = useState(false);
  const [customsAddForm, setCustomsAddForm] = useState(emptyCustomsAdd());

  // --- Broker Fees state ---
  const [brokerFees, setBrokerFees] = useState([]);
  const [brokerLoading, setBrokerLoading] = useState(true);
  const [brokerError, setBrokerError] = useState(null);
  const [brokerEditingId, setBrokerEditingId] = useState(null);
  const [brokerEditForm, setBrokerEditForm] = useState({});
  const [brokerSaving, setBrokerSaving] = useState(false);
  const [brokerFeedback, setBrokerFeedback] = useState(null);
  const [showBrokerAdd, setShowBrokerAdd] = useState(false);
  const [brokerAddForm, setBrokerAddForm] = useState(emptyBrokerAdd());

  // --- Empty form templates ---
  function emptyCustomsAdd() {
    return {
      hsCode: '',
      originCountry: '',
      baseDutyPercent: '',
      section301Percent: '',
      additionalTariffPercent: '',
      productCategory: '',
      notes: ''
    };
  }

  function emptyBrokerAdd() {
    return {
      forwarder: '',
      feeName: '',
      amount: '',
      notes: ''
    };
  }

  // ========================================
  // US Customs Rates logic
  // ========================================
  const fetchCustomsRates = useCallback(async () => {
    try {
      setCustomsLoading(true);
      setCustomsError(null);
      const result = await usCustomsRateAPI.getAll();
      setCustomsRates(result.data || []);
    } catch (err) {
      setCustomsError('Failed to load US customs rates');
      console.error('UsCustomsConfig customs fetch error:', err);
    } finally {
      setCustomsLoading(false);
    }
  }, []);

  const handleCustomsEdit = (rate) => {
    setCustomsEditingId(rate.id);
    setCustomsEditForm({
      hsCode: rate.hsCode || '',
      originCountry: rate.originCountry || '',
      baseDutyPercent: rate.baseDutyPercent ?? '',
      section301Percent: rate.section301Percent ?? '',
      additionalTariffPercent: rate.additionalTariffPercent ?? '',
      productCategory: rate.productCategory || '',
      notes: rate.notes || ''
    });
    setCustomsFeedback(null);
  };

  const handleCustomsCancel = () => {
    setCustomsEditingId(null);
    setCustomsEditForm({});
  };

  const handleCustomsChange = (field, value) => {
    setCustomsEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCustomsSave = async () => {
    try {
      setCustomsSaving(true);
      setCustomsFeedback(null);

      const data = {
        ...customsEditForm,
        baseDutyPercent: customsEditForm.baseDutyPercent !== '' ? Number(customsEditForm.baseDutyPercent) : undefined,
        section301Percent: customsEditForm.section301Percent !== '' ? Number(customsEditForm.section301Percent) : undefined,
        additionalTariffPercent: customsEditForm.additionalTariffPercent !== '' ? Number(customsEditForm.additionalTariffPercent) : undefined
      };

      await usCustomsRateAPI.update(customsEditingId, data);
      setCustomsFeedback({ type: 'success', message: 'Customs rate updated successfully' });
      setCustomsEditingId(null);
      setCustomsEditForm({});
      await fetchCustomsRates();
    } catch (err) {
      setCustomsFeedback({ type: 'error', message: 'Failed to update customs rate' });
      console.error('UsCustomsConfig customs save error:', err);
    } finally {
      setCustomsSaving(false);
    }
  };

  const handleCustomsAddChange = (field, value) => {
    setCustomsAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCustomsAddSubmit = async (e) => {
    e.preventDefault();
    try {
      setCustomsSaving(true);
      setCustomsFeedback(null);

      const data = {
        ...customsAddForm,
        baseDutyPercent: customsAddForm.baseDutyPercent !== '' ? Number(customsAddForm.baseDutyPercent) : undefined,
        section301Percent: customsAddForm.section301Percent !== '' ? Number(customsAddForm.section301Percent) : undefined,
        additionalTariffPercent: customsAddForm.additionalTariffPercent !== '' ? Number(customsAddForm.additionalTariffPercent) : undefined
      };

      await usCustomsRateAPI.create(data);
      setCustomsFeedback({ type: 'success', message: 'Customs rate created successfully' });
      setCustomsAddForm(emptyCustomsAdd());
      setShowCustomsAdd(false);
      await fetchCustomsRates();
    } catch (err) {
      setCustomsFeedback({ type: 'error', message: 'Failed to create customs rate' });
      console.error('UsCustomsConfig customs add error:', err);
    } finally {
      setCustomsSaving(false);
    }
  };

  // ========================================
  // Broker Fees logic
  // ========================================
  const fetchBrokerFees = useCallback(async () => {
    try {
      setBrokerLoading(true);
      setBrokerError(null);
      const result = await customsBrokerFeeAPI.getAll();
      setBrokerFees(result.data || []);
    } catch (err) {
      setBrokerError('Failed to load customs broker fees');
      console.error('UsCustomsConfig broker fetch error:', err);
    } finally {
      setBrokerLoading(false);
    }
  }, []);

  const handleBrokerEdit = (fee) => {
    setBrokerEditingId(fee.id);
    setBrokerEditForm({
      forwarder: fee.forwarder || '',
      feeName: fee.feeName || '',
      amount: fee.amount ?? '',
      notes: fee.notes || ''
    });
    setBrokerFeedback(null);
  };

  const handleBrokerCancel = () => {
    setBrokerEditingId(null);
    setBrokerEditForm({});
  };

  const handleBrokerChange = (field, value) => {
    setBrokerEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleBrokerSave = async () => {
    try {
      setBrokerSaving(true);
      setBrokerFeedback(null);

      const data = {
        ...brokerEditForm,
        amount: brokerEditForm.amount !== '' ? Number(brokerEditForm.amount) : undefined
      };

      await customsBrokerFeeAPI.update(brokerEditingId, data);
      setBrokerFeedback({ type: 'success', message: 'Broker fee updated successfully' });
      setBrokerEditingId(null);
      setBrokerEditForm({});
      await fetchBrokerFees();
    } catch (err) {
      setBrokerFeedback({ type: 'error', message: 'Failed to update broker fee' });
      console.error('UsCustomsConfig broker save error:', err);
    } finally {
      setBrokerSaving(false);
    }
  };

  const handleBrokerAddChange = (field, value) => {
    setBrokerAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleBrokerAddSubmit = async (e) => {
    e.preventDefault();
    try {
      setBrokerSaving(true);
      setBrokerFeedback(null);

      const data = {
        ...brokerAddForm,
        amount: brokerAddForm.amount !== '' ? Number(brokerAddForm.amount) : undefined
      };

      await customsBrokerFeeAPI.create(data);
      setBrokerFeedback({ type: 'success', message: 'Broker fee created successfully' });
      setBrokerAddForm(emptyBrokerAdd());
      setShowBrokerAdd(false);
      await fetchBrokerFees();
    } catch (err) {
      setBrokerFeedback({ type: 'error', message: 'Failed to create broker fee' });
      console.error('UsCustomsConfig broker add error:', err);
    } finally {
      setBrokerSaving(false);
    }
  };

  // --- Load data on mount ---
  useEffect(() => {
    fetchCustomsRates();
    fetchBrokerFees();
  }, [fetchCustomsRates, fetchBrokerFees]);

  const formatPercent = (val) => {
    if (val == null || val === '') return '-';
    return `${Number(val).toFixed(2)}%`;
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>US Customs Configuration</h1>
      </div>

      {/* ===== Section 1: US Customs Rates ===== */}
      <div style={{ marginBottom: '3rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>US Customs Rates (by HS Code / Origin)</h2>
          <button className="btn btn-primary" onClick={() => setShowCustomsAdd(!showCustomsAdd)}>
            {showCustomsAdd ? 'Cancel' : 'Add Rate'}
          </button>
        </div>

        {customsFeedback && (
          <div className={customsFeedback.type === 'success' ? 'success-banner' : 'error-message'}
            style={{ marginBottom: '1rem' }}>
            <p>{customsFeedback.message}</p>
          </div>
        )}

        {/* Add customs rate form */}
        {showCustomsAdd && (
          <form onSubmit={handleCustomsAddSubmit} className="detail-section" style={{ marginBottom: '1.5rem' }}>
            <h3>New Customs Rate</h3>
            <div className="info-grid" style={{ gap: '0.75rem' }}>
              <div className="info-item">
                <label>HS Code *</label>
                <input className="admin-inline-input" type="text" required
                  value={customsAddForm.hsCode} onChange={(e) => handleCustomsAddChange('hsCode', e.target.value)} />
              </div>
              <div className="info-item">
                <label>Origin Country *</label>
                <input className="admin-inline-input" type="text" required
                  value={customsAddForm.originCountry} onChange={(e) => handleCustomsAddChange('originCountry', e.target.value)} />
              </div>
              <div className="info-item">
                <label>Base Duty % *</label>
                <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01" required
                  value={customsAddForm.baseDutyPercent} onChange={(e) => handleCustomsAddChange('baseDutyPercent', e.target.value)} />
              </div>
              <div className="info-item">
                <label>Section 301 %</label>
                <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01"
                  value={customsAddForm.section301Percent} onChange={(e) => handleCustomsAddChange('section301Percent', e.target.value)} />
              </div>
              <div className="info-item">
                <label>Additional Tariff %</label>
                <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01"
                  value={customsAddForm.additionalTariffPercent} onChange={(e) => handleCustomsAddChange('additionalTariffPercent', e.target.value)} />
              </div>
              <div className="info-item">
                <label>Product Category</label>
                <input className="admin-inline-input" type="text"
                  value={customsAddForm.productCategory} onChange={(e) => handleCustomsAddChange('productCategory', e.target.value)} />
              </div>
              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <label>Notes</label>
                <input className="admin-inline-input" type="text"
                  value={customsAddForm.notes} onChange={(e) => handleCustomsAddChange('notes', e.target.value)} />
              </div>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button className="btn btn-primary" type="submit" disabled={customsSaving}>
                {customsSaving ? 'Saving...' : 'Create Rate'}
              </button>
            </div>
          </form>
        )}

        {customsLoading ? (
          <div className="loading">Loading customs rates...</div>
        ) : customsError ? (
          <div className="error-message">{customsError}</div>
        ) : customsRates.length === 0 ? (
          <div className="empty-state">
            <p>No US customs rates configured yet.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="simple-table">
              <thead>
                <tr>
                  <th>HS Code</th>
                  <th>Origin Country</th>
                  <th>Base Duty %</th>
                  <th>Section 301 %</th>
                  <th>Addl Tariff %</th>
                  <th>Product Category</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customsRates.map((rate) => (
                  <tr key={rate.id}>
                    {customsEditingId === rate.id ? (
                      <>
                        <td>
                          <input className="admin-inline-input" type="text"
                            value={customsEditForm.hsCode} onChange={(e) => handleCustomsChange('hsCode', e.target.value)} />
                        </td>
                        <td>
                          <input className="admin-inline-input" type="text"
                            value={customsEditForm.originCountry} onChange={(e) => handleCustomsChange('originCountry', e.target.value)} />
                        </td>
                        <td>
                          <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01"
                            value={customsEditForm.baseDutyPercent} onChange={(e) => handleCustomsChange('baseDutyPercent', e.target.value)} />
                        </td>
                        <td>
                          <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01"
                            value={customsEditForm.section301Percent} onChange={(e) => handleCustomsChange('section301Percent', e.target.value)} />
                        </td>
                        <td>
                          <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01"
                            value={customsEditForm.additionalTariffPercent} onChange={(e) => handleCustomsChange('additionalTariffPercent', e.target.value)} />
                        </td>
                        <td>
                          <input className="admin-inline-input" type="text"
                            value={customsEditForm.productCategory} onChange={(e) => handleCustomsChange('productCategory', e.target.value)} />
                        </td>
                        <td>
                          <input className="admin-inline-input" type="text"
                            value={customsEditForm.notes} onChange={(e) => handleCustomsChange('notes', e.target.value)} />
                        </td>
                        <td className="actions">
                          <button className="btn btn-sm btn-primary" onClick={handleCustomsSave} disabled={customsSaving}>
                            {customsSaving ? 'Saving...' : 'Save'}
                          </button>
                          <button className="btn btn-sm btn-secondary" onClick={handleCustomsCancel} disabled={customsSaving}>
                            Cancel
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td><code>{rate.hsCode || '-'}</code></td>
                        <td>{rate.originCountry || '-'}</td>
                        <td>{formatPercent(rate.baseDutyPercent)}</td>
                        <td>{formatPercent(rate.section301Percent)}</td>
                        <td>{formatPercent(rate.additionalTariffPercent)}</td>
                        <td className="text-small">{rate.productCategory || '-'}</td>
                        <td className="text-small">{rate.notes || '-'}</td>
                        <td className="actions">
                          <button className="btn btn-sm btn-secondary" onClick={() => handleCustomsEdit(rate)}>
                            Edit
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== Section 2: Customs Broker Fees ===== */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>Customs Broker Fees (by Forwarder)</h2>
          <button className="btn btn-primary" onClick={() => setShowBrokerAdd(!showBrokerAdd)}>
            {showBrokerAdd ? 'Cancel' : 'Add Fee'}
          </button>
        </div>

        {brokerFeedback && (
          <div className={brokerFeedback.type === 'success' ? 'success-banner' : 'error-message'}
            style={{ marginBottom: '1rem' }}>
            <p>{brokerFeedback.message}</p>
          </div>
        )}

        {/* Add broker fee form */}
        {showBrokerAdd && (
          <form onSubmit={handleBrokerAddSubmit} className="detail-section" style={{ marginBottom: '1.5rem' }}>
            <h3>New Broker Fee</h3>
            <div className="info-grid" style={{ gap: '0.75rem' }}>
              <div className="info-item">
                <label>Forwarder *</label>
                <input className="admin-inline-input" type="text" required
                  value={brokerAddForm.forwarder} onChange={(e) => handleBrokerAddChange('forwarder', e.target.value)} />
              </div>
              <div className="info-item">
                <label>Fee Name *</label>
                <input className="admin-inline-input" type="text" required placeholder="e.g. Customs Clearance, ISF, CFC"
                  value={brokerAddForm.feeName} onChange={(e) => handleBrokerAddChange('feeName', e.target.value)} />
              </div>
              <div className="info-item">
                <label>Amount ($) *</label>
                <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01" required
                  value={brokerAddForm.amount} onChange={(e) => handleBrokerAddChange('amount', e.target.value)} />
              </div>
              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <label>Notes</label>
                <input className="admin-inline-input" type="text"
                  value={brokerAddForm.notes} onChange={(e) => handleBrokerAddChange('notes', e.target.value)} />
              </div>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button className="btn btn-primary" type="submit" disabled={brokerSaving}>
                {brokerSaving ? 'Saving...' : 'Create Fee'}
              </button>
            </div>
          </form>
        )}

        {brokerLoading ? (
          <div className="loading">Loading broker fees...</div>
        ) : brokerError ? (
          <div className="error-message">{brokerError}</div>
        ) : brokerFees.length === 0 ? (
          <div className="empty-state">
            <p>No customs broker fees configured yet.</p>
          </div>
        ) : (
          <table className="simple-table">
            <thead>
              <tr>
                <th>Forwarder</th>
                <th>Fee Name</th>
                <th>Amount</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {brokerFees.map((fee) => (
                <tr key={fee.id}>
                  {brokerEditingId === fee.id ? (
                    <>
                      <td>
                        <input className="admin-inline-input" type="text"
                          value={brokerEditForm.forwarder} onChange={(e) => handleBrokerChange('forwarder', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input" type="text"
                          value={brokerEditForm.feeName} onChange={(e) => handleBrokerChange('feeName', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01"
                          value={brokerEditForm.amount} onChange={(e) => handleBrokerChange('amount', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input" type="text"
                          value={brokerEditForm.notes} onChange={(e) => handleBrokerChange('notes', e.target.value)} />
                      </td>
                      <td className="actions">
                        <button className="btn btn-sm btn-primary" onClick={handleBrokerSave} disabled={brokerSaving}>
                          {brokerSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={handleBrokerCancel} disabled={brokerSaving}>
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{fee.forwarder || '-'}</td>
                      <td>{fee.feeName || '-'}</td>
                      <td>{fee.amount != null ? `$${Number(fee.amount).toFixed(2)}` : '-'}</td>
                      <td className="text-small">{fee.notes || '-'}</td>
                      <td className="actions">
                        <button className="btn btn-sm btn-secondary" onClick={() => handleBrokerEdit(fee)}>
                          Edit
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default UsCustomsConfig;
